<template>
  <AppNavigation mode="topbar"/>
  <div class="blog-list-container">
    <h1>{{ $t("navigation.blogs") }}</h1>
    <div class="blog-grid">
      <router-link
        v-for="post in blogPosts"
        :key="post.slug"
        :to="'/blog/' + post.slug"
        class="blog-card"
      >
        <div class="blog-card-image" v-if="post.heroImage">
          <img :src="post.heroImage" :alt="post.title" lang="en" />
        </div>
        <!-- Article titles and bodies are authored in English and stay English
             by decision, so they carry lang="en" while the document around them
             follows the reader's locale. Without it a screen reader announces
             English prose with (say) a Japanese voice. -->
        <div class="blog-card-body">
          <h2 class="blog-card-title" lang="en">{{ post.title }}</h2>
          <p class="blog-card-date">{{ formatDate(post.date) }}</p>
          <p class="blog-card-excerpt" lang="en">{{ getExcerpt(post.content) }}</p>
        </div>
      </router-link>
    </div>
  </div>
  <FooterSection/>
</template>

<script>
import AppNavigation from "@/components/AppNavigation.vue";
import FooterSection from "@/components/FooterSection.vue";
import { computed } from "vue";
import { useHead } from "@unhead/vue";
import { publicPageHead } from "@/scripts/publicHead.js";
import { authUtils } from "@/scripts/authUtils.js";
import { blogPosts } from "@/data/blog/index.js";
import { formatLongDate } from "@/scripts/dateUtils.js";

export default {
  name: "BlogListView",
  components: { AppNavigation, FooterSection },
  data() {
    return { blogPosts };
  },
  methods: {
    formatDate(dateStr) {
      // "T00:00:00" pins the day to local midnight; parsing a bare "YYYY-MM-DD"
      // is UTC by spec and lands on the previous day west of Greenwich.
      return formatLongDate(dateStr + "T00:00:00", this.$i18n.locale);
    },
    getExcerpt(html) {
      const text = html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ");
      if (text.length <= 150) return text;
      return text.substring(0, 150).replace(/\s+\S*$/, "") + "...";
    },
  },
  mounted() {
    authUtils.waitForAuthInitialization();
    // computed, not a plain string: locale bundles load lazily, so a value read
    // at mount can freeze the English fallback into the tab and the og: card.
    // No htmlAttrs.lang anywhere in here: <html lang> is owned by the locale
    // watcher in src/locales/index.js and describes the interface the reader is
    // using. Two writers would race, and the loser would be whichever ran last
    // on that particular navigation. The English article content declares itself
    // with lang="en" on its own elements instead, which is what that attribute
    // is for.
    useHead(
      publicPageHead({
        title: computed(() => this.$t("navigation.blogs")),
        description: computed(() => this.$t("blog.metaDescription")),
        path: "/blog",
      }),
    );
  },
};
</script>

<style scoped>
.blog-list-container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 100px 1rem 4rem;
}

.blog-list-container h1 {
  font-size: 2rem;
  margin-bottom: 2rem;
  color: var(--al-ink-900);
}

.blog-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.blog-card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--al-gray-300);
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.blog-card:hover {
  box-shadow: var(--al-shadow-md);
  transform: translateY(-2px);
}

.blog-card-image {
  width: 100%;
  height: 180px;
  overflow: hidden;
}

.blog-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.blog-card-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.blog-card-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--al-ink-900);
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.blog-card-date {
  font-size: 0.8rem;
  color: var(--al-gray);
  margin-bottom: 0.75rem;
}

.blog-card-excerpt {
  font-size: 0.88rem;
  color: var(--al-gray-700);
  line-height: 1.5;
  flex: 1;
}

@media (max-width: 1023px) {
  .blog-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .blog-grid {
    grid-template-columns: 1fr;
  }

  .blog-list-container {
    padding-top: calc(var(--al-topbar-height) + 24px);
  }
}
</style>
